import { CalendarDays } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  IntegrationRow,
  IntegrationStatus,
} from '@/modules/hiring-manager/components/IntegrationRow';
import { useConnectGoogleCalendar } from '@/modules/integrations/hooks/useConnectGoogleCalendar';
import { Button } from '@/modules/ui/components/button/Button';
import { ConfirmDialog } from '@/modules/ui/components/confirm-dialog/ConfirmDialog';
import { useDeleteUserExternalAccountMutation } from '@/modules/user-external-accounts/mutations/useDeleteUserExternalAccountMutation';
import type { components } from '@/types/apiSchema';
import { actionErrorMessage } from '@/utils/actionErrorMessage';

type ExternalAccount = components['schemas']['UserExternalAccountResponse'];

interface GoogleCalendarRowProps {
  userId: string;
  /** The user's Google Calendar account, if one was ever connected. */
  account?: ExternalAccount;
}

/**
 * Google Calendar: Finlay reads the HM's busy times so offered interview
 * slots don't clash. The API refuses a second connect while one is active,
 * so a connected row only offers Disconnect.
 */
export function GoogleCalendarRow({ userId, account }: GoogleCalendarRowProps) {
  const connect = useConnectGoogleCalendar();
  const disconnectMutation = useDeleteUserExternalAccountMutation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isDisconnectDialogOpen, setIsDisconnectDialogOpen] = useState(false);
  // The OAuth callback lands here with the result; keep a failure on the row
  // after the param is stripped, so a reload doesn't repeat it.
  const [connectFailed] = useState(
    () => searchParams.get('calendarConnection') === 'failure',
  );

  useEffect(() => {
    if (!searchParams.has('calendarConnection')) return;
    const next = new URLSearchParams(searchParams);
    next.delete('calendarConnection');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const isConnected = account?.status === 'Active';

  const openDisconnectDialog = () => {
    // Reopening starts clean instead of showing the last attempt's error.
    disconnectMutation.reset();
    setIsDisconnectDialogOpen(true);
  };

  return (
    <>
      <IntegrationRow
        action={
          isConnected ? (
            <Button
              className="h-8 rounded-md"
              onClick={openDisconnectDialog}
              size="sm"
              variant="outline"
            >
              Disconnect
            </Button>
          ) : (
            <Button
              className="h-8 rounded-md"
              onClick={connect}
              size="sm"
              variant="primary"
            >
              Connect Google Calendar
            </Button>
          )
        }
        description="Finlay checks when you're busy in your primary calendar, so the interview times it offers candidates don't clash."
        icon={CalendarDays}
        name="Google Calendar"
        note={
          connectFailed && !isConnected
            ? "Google Calendar couldn't be connected. Try again."
            : undefined
        }
        status={
          <IntegrationStatus isActive={isConnected}>
            {isConnected
              ? `Connected${account?.email ? ` · ${account.email}` : ''}`
              : 'Not connected'}
          </IntegrationStatus>
        }
        usedBy="Interview scheduling"
      />

      <ConfirmDialog
        confirmText="Disconnect"
        confirming={disconnectMutation.isPending}
        confirmingText="Disconnecting…"
        description="Finlay stops reading your calendar right away. Interview times it offers won't be checked against it until you connect again."
        error={
          disconnectMutation.isError
            ? actionErrorMessage(
                'disconnect Google Calendar',
                disconnectMutation.error,
              )
            : null
        }
        onConfirm={() =>
          account &&
          disconnectMutation.mutate(
            { userId, id: account.id },
            { onSuccess: () => setIsDisconnectDialogOpen(false) },
          )
        }
        onOpenChange={(open) =>
          !disconnectMutation.isPending && setIsDisconnectDialogOpen(open)
        }
        open={isDisconnectDialogOpen}
        title="Disconnect Google Calendar?"
        variant="destructive"
      />
    </>
  );
}
